import React from "react";
import { createTheme, alpha, darken, lighten } from "@mui/material/styles";
import type { ThemeOptions, Theme } from "@mui/material/styles";
import TypographyWithLinkify from "./TypographyWithLinkify";

import type { ApiTheme } from "@/types/theme";
import {
  resolveThemeTokens,
  resolveWebFontFamily,
} from "./resolveThemeTokens";

export { themeColors } from "./themeColors";
export { deriveBorderColor, deriveMutedSurface } from "./resolveThemeTokens";
export type { MergedColors } from "./resolveThemeTokens";
export type { ThemePaletteColors } from "./themeColors";

// Augment MUI theme to include MUI X Date Pickers component overrides
declare module "@mui/material/styles" {
  interface Palette {
    input: { main: string };
  }
  interface PaletteOptions {
    input?: { main?: string };
  }
  interface ComponentNameToClassKey {
    MuiPickersDay: Record<string, string>;
    MuiPickersArrowSwitcher: Record<string, string>;
    MuiPickersCalendarHeader: Record<string, string>;
  }
  interface ComponentsPropsList {
    MuiPickersDay: Record<string, unknown>;
    MuiPickersArrowSwitcher: Record<string, unknown>;
    MuiPickersCalendarHeader: Record<string, unknown>;
  }
  interface Components {
    MuiPickersDay?: {
      styleOverrides?: Record<string, unknown>;
    };
    MuiPickersArrowSwitcher?: {
      styleOverrides?: Record<string, unknown>;
    };
    MuiPickersCalendarHeader?: {
      styleOverrides?: Record<string, unknown>;
    };
  }
}

export type ThemeMode = "light" | "dark" | "personal" | "system";

/**
 * MUI v7 buttons use --variant-* CSS variables; sync them with branded foreground.
 */
function brandedButtonLabelVars(theme: Theme) {
  const label = theme.palette.text.primary;
  return {
    color: label,
    "--variant-outlinedColor": label,
    "--variant-textColor": label,
  };
}

export const createAppTheme = (mode: ThemeMode = "light", apiTheme?: ApiTheme | null) => {
  const actualMode: "light" | "dark" =
    mode === "dark" ? "dark" : "light";
  const { colors } = resolveThemeTokens(actualMode, apiTheme);

  const themeOptions: ThemeOptions = {
    palette: {
      mode: actualMode,
      primary: {
        main: colors.primary,
        light: lighten(colors.primary, 0.2),
        dark: darken(colors.primary, 0.15),
        contrastText: colors.primaryForeground,
      },
      secondary: {
        main: colors.secondary ?? colors.success,
        light: lighten(colors.secondary ?? colors.success, 0.2),
        dark: darken(colors.secondary ?? colors.success, 0.15),
        contrastText: apiTheme?.secondaryForeground ?? "#FFFFFF",
      },
      background: {
        default: colors.background,
        paper: colors.card,
      },
      text: {
        primary: colors.foreground,
        secondary: colors.mutedForeground,
      },
      success: {
        main: colors.success,
        dark: darken(colors.success, 0.12),
        light: lighten(colors.success, 0.15),
      },
      warning: {
        // Primary (gold) for MUI X date pickers; snackbar warning uses --color-warning in NotificationProvider
        main: colors.primary,
        dark: darken(colors.primary, 0.12),
        light: lighten(colors.primary, 0.15),
      },
      error: {
        main: colors.danger,
        dark: darken(colors.danger, 0.12),
        light: lighten(colors.danger, 0.15),
      },
      info: {
        main: actualMode === "dark" ? "#64b5f6" : "#2196f3",
        dark: actualMode === "dark" ? "#42a5f5" : "#1976d2",
        light: actualMode === "dark" ? "#90caf9" : "#64b5f6",
      },
      divider: colors.border,
      input: {
        main: colors.muted,
      },
      action: {
        hover: alpha(colors.foreground, 0.06),
        selected: alpha(colors.foreground, 0.1),
        disabled: alpha(colors.foreground, 0.38),
        disabledBackground: alpha(colors.foreground, 0.12),
        focus: alpha(colors.foreground, 0.12),
      },
    },
    typography: {
      fontFamily: resolveWebFontFamily(apiTheme),
      fontSize: 16,
      h1: { fontWeight: 700, fontSize: "var(--fs-xl)", lineHeight: 1.15 },
      h2: {
        fontWeight: 600,
        fontSize: "calc(var(--fs-lg) + 0.15rem)",
        lineHeight: 1.2,
      },
      h3: { fontWeight: 600, fontSize: "var(--fs-lg)", lineHeight: 1.25 },
      h4: {
        fontWeight: 600,
        fontSize: "calc(var(--fs-md) + 0.25rem)",
        lineHeight: 1.3,
      },
      h5: {
        fontWeight: 600,
        fontSize: "calc(var(--fs-md) + 0.1rem)",
        lineHeight: 1.35,
      },
      h6: { fontWeight: 600, fontSize: "var(--fs-md)", lineHeight: 1.4 },
      body1: { fontSize: "var(--fs-md)", lineHeight: 1.6 },
      body2: { fontSize: "var(--fs-sm)", lineHeight: 1.55 },
      subtitle1: { fontSize: "calc(var(--fs-sm) + 0.05rem)" },
      subtitle2: { fontSize: "var(--fs-sm)" },
      button: { fontSize: "var(--fs-sm)" },
      caption: { fontSize: "var(--fs-xs)" },
      overline: { fontSize: "var(--fs-xs)" },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background,
            color: colors.foreground,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          component: TypographyWithLinkify as React.ComponentType<any>,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
            padding: "calc(var(--space-2) + 0.125rem) var(--space-5)",
            fontSize: "var(--fs-sm)",
            minHeight: "2.75rem" /* >=44px hit target */,
          },
          textPrimary: ({ theme }) => ({
            ...brandedButtonLabelVars(theme),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
            "&:active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
            },
          }),
          outlinedPrimary: ({ theme }) => ({
            ...brandedButtonLabelVars(theme),
            borderColor: alpha(theme.palette.primary.main, 0.5),
            "--variant-outlinedBorder": alpha(theme.palette.primary.main, 0.5),
            "&:hover": {
              borderColor: theme.palette.primary.main,
              "--variant-outlinedBorder": theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              "--variant-outlinedBg": alpha(theme.palette.primary.main, 0.08),
            },
            "&:active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              "--variant-outlinedBg": alpha(theme.palette.primary.main, 0.12),
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              borderColor: theme.palette.action.disabledBackground,
            },
          }),
          outlinedSecondary: ({ theme }) => ({
            ...brandedButtonLabelVars(theme),
            borderColor: alpha(theme.palette.secondary.main, 0.5),
            "--variant-outlinedBorder": alpha(theme.palette.secondary.main, 0.5),
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.card,
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            borderRadius: 12,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              transform: "translateY(-2px)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.card,
            backgroundImage: "none",
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
          selected: ({ theme }) => ({
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: `${theme.palette.primary.dark} !important`,
            },
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            // Navigation chrome uses page background (API "Fond"), not "Carte".
            backgroundColor: colors.background,
            color: colors.foreground,
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            borderBottom: `1px solid ${colors.border}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background,
            borderRight: `1px solid ${colors.border}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            backgroundImage: "none",
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.input.main,
          }),
          input: ({ theme }) => ({
            "&:-webkit-autofill": {
              WebkitBoxShadow: `0 0 0 100px ${theme.palette.input.main} inset`,
              WebkitTextFillColor: theme.palette.text.primary,
              caretColor: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.input.main,
            "& fieldset": {
              borderColor: theme.palette.divider,
            },
            "&:hover fieldset": {
              borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
              borderColor: theme.palette.primary.main,
            },
          }),
          input: ({ theme }) => ({
            "&::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.input.main,
              "& fieldset": {
                borderColor: theme.palette.divider,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.MuiChip-outlined": {
              backgroundColor: theme.palette.input.main,
              borderColor: theme.palette.divider,
            },
            "&.MuiChip-outlinedDefault": {
              backgroundColor: theme.palette.input.main,
            },
            "&.MuiChip-filledDefault": {
              backgroundColor: theme.palette.input.main,
              color: theme.palette.text.primary,
            },
            "&.MuiChip-outlinedPrimary": {
              backgroundColor: alpha(theme.palette.primary.main, 0.14),
              borderColor: alpha(theme.palette.primary.main, 0.45),
              color: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: ({ theme }) => ({
            backgroundColor: theme.palette.input.main,
          }),
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: ({ theme }) => ({
            backgroundColor: theme.palette.input.main,
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            textTransform: "none",
            fontWeight: 600,
            "&.Mui-selected": {
              color: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            backgroundImage: "none",
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            "&.Mui-focused": { color: theme.palette.primary.main },
          }),
        },
      },
      // MUI X Date Pickers - brand yellow theme
      MuiPickersDay: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            "&.is-taken": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.warning.main, 0.22)
                  : alpha(theme.palette.warning.main, 0.15),
              color: theme.palette.text.primary,
              borderRadius: 4,
            },
            "&.Mui-selected, &.Mui-selected:focus, &.Mui-selected:hover": {
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.getContrastText(theme.palette.warning.main),
            },
            "&.MuiPickersDay-today": {
              border: `1px solid ${theme.palette.warning.main}`,
            },
          }),
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: ({ theme }: { theme: Theme }) => ({
            color:
              theme.palette.mode === "dark"
                ? theme.palette.warning.main
                : theme.palette.text.secondary,
            "&:hover": {
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.warning.main
                  : theme.palette.text.primary,
            },
          }),
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          label: ({ theme }: { theme: Theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Service colors for different features (fallbacks when theme not available)
export const serviceColors = {
  appointments: "#FFD700", // Gold fallback
  calendar: "#00C853",
  meetings: "#06D6A0", // Green
  concierge: "#4361EE", // Blue
  bar: "#FF6B35", // Orange
  lending: "#00897B", // Teal — catalogue prêts
  services: "#00897B", // Teal — réservations de services
  events: "#F72585", // Pink
  gym: "#FF6B35", // Orange
  beauty: "#7209B7", // Purple
  ticketing: "#E53935", // Red — signalement
  profile: "#6B7280", // Gray
  history: "#9CA3AF",
  messaging: "#2563EB", // Blue — messagerie
  // ticketing defined above (single key)
};

/** Nav icons: branded primary/secondary + shared service accents */
export function getNavServiceColors(primary: string, secondary: string) {
  return {
    ...serviceColors,
    appointments: primary,
    meetings: secondary,
  };
}

/** Readable text on arbitrary hex backgrounds (event cards, room colors) */
export function getContrastTextForHex(
  hexColor: string,
  fallback = "#1A1A1A",
): string {
  const hex = hexColor.replace("#", "").trim();
  if (hex.length !== 6) return fallback;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return fallback;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}

// Calendar colors for light and dark themes
export const calendarColors = {
  light: {
    appointment: {
      full: "#3B82F6", // Blue - more professional and readable
      transparent: "#3B82F640", // Blue - 40% opacity
      background: "#EFF6FF", // Light blue background
      text: "#1E293B", // Dark slate text for excellent readability
    },
    event: {
      full: "#8B5CF6", // Purple - elegant and distinct
      transparent: "#8B5CF640", // Purple - 40% opacity
      background: "#F5F3FF", // Light purple background
      text: "#1E293B", // Dark slate text for excellent readability
    },
    meeting: {
      full: "#10B981", // Emerald green - professional and clear
      transparent: "#10B98140", // Emerald - 40% opacity
      background: "#ECFDF5", // Light emerald background
      text: "#1E293B", // Dark slate text for excellent readability
    },
    service: {
      full: "#B8F2D8", // Soft mint — réservations de services
      transparent: "#B8F2D840",
      background: "#ECFDF5",
      text: "#1E293B",
    },
  },
  dark: {
    appointment: {
      full: "#60A5FA", // Lighter blue for dark mode
      transparent: "#60A5FA70", // Blue - 70% opacity (more visible in dark)
      background: "#1E3A8A", // Dark blue background
      text: "#F8FAFC", // Light text for excellent contrast
    },
    event: {
      full: "#A78BFA", // Lighter purple for dark mode
      transparent: "#A78BFA70", // Purple - 70% opacity (more visible in dark)
      background: "#581C87", // Dark purple background
      text: "#F8FAFC", // Light text for excellent contrast
    },
    meeting: {
      full: "#34D399", // Lighter emerald for dark mode
      transparent: "#34D39970", // Emerald - 70% opacity (more visible in dark)
      background: "#064E3B", // Dark emerald background
      text: "#F8FAFC", // Light text for excellent contrast
    },
    service: {
      full: "#1F8A5B",
      transparent: "#1F8A5B70",
      background: "#134E4A",
      text: "#F8FAFC",
    },
  },
};

/** Calendar summary chips / legend dots */
export function getCalendarServiceColors(mode: "light" | "dark") {
  const palette = mode === "dark" ? calendarColors.dark : calendarColors.light;
  return {
    appointments: palette.appointment.full,
    meetings: palette.meeting.full,
    events: palette.event.full,
    services: palette.service.full,
  };
}

export default createAppTheme;
